/**
 * Vessel Risk Register metinleri (K4; ADR 0088): gemiye atanmış güncel şirket RA sürümleri,
 * okundu onayı ve eski sürümdeki gemi RA'ları. Adlar ortağın belgelerindeki gibi İngilizce (FD §2,
 * §12); Türkçe ikinci dil.
 */

export const riskRegisterEn = {
  title: 'Vessel Risk Register',
  description:
    'Company risk assessments currently assigned to the vessel, their revision, review date and acknowledgement on board.',
  shipNote:
    'The master or an authorised officer reads each new or revised company risk assessment and acknowledges it on behalf of the vessel. Versions migrated from the former templates need no acknowledgement.',
  officeNote:
    'Acknowledgements are given on board and arrive with synchronisation; the office can only view them.',
  vessel: 'Vessel',
  stats: {
    current: 'Current Assessments',
    pending: 'Awaiting Acknowledgement',
    overdue: 'Review Overdue',
    outdated: 'Vessel RAs on Old Versions',
  },
  search: 'Search by ID, title or vessel RA number',
  filter: {
    state: 'Acknowledgement',
    states: {
      all: 'All',
      pending: 'Awaiting acknowledgement',
      acknowledged: 'Acknowledged',
      not_required: 'Not required',
      review: 'Review due or overdue',
    },
  },
  columns: {
    code: 'Assessment ID',
    title: 'Title',
    revision: 'Revision',
    published: 'Published',
    basis: 'Assigned By',
    residual: 'Target Residual',
    review: 'Review',
    acknowledgement: 'Acknowledgement',
    assessments: 'Vessel RAs',
  },
  basis: {
    vessel: 'This vessel',
    group: 'Vessel type: {{group}}',
    all: 'All vessels',
  },
  acknowledgement: {
    acknowledged: 'Acknowledged',
    pending: 'Awaiting acknowledgement',
    not_required: 'Not required',
  },
  acknowledgedBy: '{{name}} · {{role}}',
  review: {
    due: 'Review due',
    overdue: 'Review overdue',
  },
  migrated: 'Migrated from template',
  tableLabel: 'Current company risk assessments',
  showing: 'Showing {{from}} to {{to}} of {{total}} assessments',
  pagination: 'Register pages',
  open: 'Open {{code}}',
  readAndAcknowledge: 'Read & Acknowledge',
  none: '—',
  outdated: {
    title: 'Vessel Risk Assessments on Superseded Versions',
    description:
      'These vessel risk assessments started from an earlier company version. Review them against the new version.',
    tableLabel: 'Vessel RAs started from earlier versions',
    columns: {
      raNo: 'RA No',
      title: 'Title',
      status: 'Status',
      from: 'Started From',
      latest: 'Current Version',
    },
    flag: 'New version available',
    notAssigned: 'Not assigned to this vessel',
  },
  empty: {
    title: 'No current assessments',
    ship: 'No company risk assessment is assigned to this vessel yet.',
    office: 'No company risk assessment is assigned to this vessel.',
    noMatch: 'No assessments match the filters',
    error: 'The register could not be loaded',
    noVessel: 'There is no vessel you can view.',
  },
  detail: {
    back: 'Back to Register',
    readOnly: 'Published version {{revision}} · read only',
    acknowledge: 'Acknowledge',
    acknowledged: 'Acknowledged by {{name}} ({{role}}) on {{time}}',
    pending: 'Read the assessment, then acknowledge it on behalf of the vessel.',
    pendingOffice: 'Awaiting acknowledgement on board.',
    notRequired: 'Migrated template version: acknowledgement is not required.',
  },
  dialog: {
    title: 'Acknowledge {{code}} {{revision}}',
    body: 'I confirm that I have read this published company risk assessment and that it applies on board. The acknowledgement is recorded with my name, rank and the time, and cannot be changed.',
    confirm: 'Acknowledge',
  },
  notice: {
    acknowledged: '{{code}} {{revision}} acknowledged',
  },
  bell: {
    pending_one: '{{count}} risk assessment version awaiting acknowledgement',
    pending_other: '{{count}} risk assessment versions awaiting acknowledgement',
  },
  errors: {
    ALREADY_ACKNOWLEDGED: 'This version has already been acknowledged on board.',
    RA_VERSION_SUPERSEDED: 'A newer version has been published; acknowledge the current version.',
    RA_VERSION_NOT_ASSIGNED: 'This version is no longer assigned to the vessel.',
    CONTENT_HASH_MISMATCH: 'The version has changed on screen; reload the register.',
    NOT_ASSIGNED: 'You are not assigned to this vessel with the rank used for the acknowledgement.',
    FORBIDDEN: 'Only the master or an authorised officer can acknowledge.',
    GENERIC: 'The action could not be completed',
  },
};

export const riskRegisterTr: typeof riskRegisterEn = {
  title: 'Gemi Risk Kaydı',
  description:
    'Gemiye şu an atanmış şirket risk değerlendirmeleri, revizyonları, gözden geçirme günleri ve gemideki okundu onayları.',
  shipNote:
    'Kaptan ya da yetkili zabit her yeni ya da revize şirket risk değerlendirmesini okur ve gemi adına onaylar. Eski şablonlardan dönüşen sürümler onay istemez.',
  officeNote: 'Okundu onayları gemide verilir ve senkronla gelir; ofis yalnız görür.',
  vessel: 'Gemi',
  stats: {
    current: 'Güncel Değerlendirmeler',
    pending: 'Onay Bekleyen',
    overdue: 'Gözden Geçirmesi Geçen',
    outdated: 'Eski Sürümdeki Gemi RA’ları',
  },
  search: 'Kimlik, başlık ya da gemi RA numarasıyla ara',
  filter: {
    state: 'Okundu Onayı',
    states: {
      all: 'Tümü',
      pending: 'Onay bekliyor',
      acknowledged: 'Onaylandı',
      not_required: 'Gerekmiyor',
      review: 'Gözden geçirmesi yaklaşan ya da geçen',
    },
  },
  columns: {
    code: 'Değerlendirme No',
    title: 'Başlık',
    revision: 'Revizyon',
    published: 'Yayım',
    basis: 'Atama',
    residual: 'Hedef Kalan Risk',
    review: 'Gözden Geçirme',
    acknowledgement: 'Okundu Onayı',
    assessments: 'Gemi RA’ları',
  },
  basis: {
    vessel: 'Bu gemi',
    group: 'Gemi tipi: {{group}}',
    all: 'Bütün gemiler',
  },
  acknowledgement: {
    acknowledged: 'Onaylandı',
    pending: 'Onay bekliyor',
    not_required: 'Gerekmiyor',
  },
  acknowledgedBy: '{{name}} · {{role}}',
  review: {
    due: 'Gözden geçirme yaklaştı',
    overdue: 'Gözden geçirme geçti',
  },
  migrated: 'Şablondan dönüştü',
  tableLabel: 'Güncel şirket risk değerlendirmeleri',
  showing: '{{total}} değerlendirmeden {{from}}–{{to}} gösteriliyor',
  pagination: 'Kayıt sayfaları',
  open: '{{code}} aç',
  readAndAcknowledge: 'Oku ve Onayla',
  none: '—',
  outdated: {
    title: 'Eski Sürümdeki Gemi Risk Değerlendirmeleri',
    description:
      'Bu gemi risk değerlendirmeleri önceki bir şirket sürümünden başladı. Yeni sürüme göre gözden geçirin.',
    tableLabel: 'Önceki sürümlerden başlayan gemi RA’ları',
    columns: {
      raNo: 'RA No',
      title: 'Başlık',
      status: 'Durum',
      from: 'Başladığı Sürüm',
      latest: 'Güncel Sürüm',
    },
    flag: 'Yeni sürüm var',
    notAssigned: 'Bu gemiye atanmadı',
  },
  empty: {
    title: 'Güncel değerlendirme yok',
    ship: 'Bu gemiye henüz bir şirket risk değerlendirmesi atanmadı.',
    office: 'Bu gemiye atanmış şirket risk değerlendirmesi yok.',
    noMatch: 'Süzgeçlere uyan değerlendirme yok',
    error: 'Kayıt okunamadı',
    noVessel: 'Görebileceğiniz bir gemi yok.',
  },
  detail: {
    back: 'Kayda Dön',
    readOnly: 'Yayımlı sürüm {{revision}} · salt okunur',
    acknowledge: 'Onayla',
    acknowledged: '{{name}} ({{role}}) {{time}} tarihinde onayladı',
    pending: 'Değerlendirmeyi okuyun, sonra gemi adına onaylayın.',
    pendingOffice: 'Gemide okundu onayı bekliyor.',
    notRequired: 'Şablondan dönüşen sürüm: okundu onayı gerekmez.',
  },
  dialog: {
    title: '{{code}} {{revision}} onayla',
    body: 'Bu yayımlı şirket risk değerlendirmesini okuduğumu ve gemide uygulandığını onaylıyorum. Onay adım, rütbem ve saatle kaydedilir; değiştirilemez.',
    confirm: 'Onayla',
  },
  notice: {
    acknowledged: '{{code}} {{revision}} onaylandı',
  },
  bell: {
    pending_one: '{{count}} risk değerlendirmesi sürümü onay bekliyor',
    pending_other: '{{count}} risk değerlendirmesi sürümü onay bekliyor',
  },
  errors: {
    ALREADY_ACKNOWLEDGED: 'Bu sürüm gemide zaten onaylandı.',
    RA_VERSION_SUPERSEDED: 'Daha yeni bir sürüm yayımlandı; güncel sürümü onaylayın.',
    RA_VERSION_NOT_ASSIGNED: 'Bu sürüm artık gemiye atanmış değil.',
    CONTENT_HASH_MISMATCH: 'Sürüm ekranda değişti; kaydı yeniden yükleyin.',
    NOT_ASSIGNED: 'Onayda kullanılan rütbeyle bu gemiye atanmış değilsiniz.',
    FORBIDDEN: 'Yalnız kaptan ya da yetkili zabit onaylayabilir.',
    GENERIC: 'İşlem tamamlanamadı',
  },
};
